import { useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	saveReflection as saveReflectionService,
	saveContributingFactors as saveContributingFactorsService,
	saveSignals as saveSignalsService,
	saveCorrectiveActions as saveCorrectiveActionsService,
	type ReflectionData as ReflectionDataService,
} from "../services/reflectionService";
import { debugLog, debugWarn } from "../utils/debug";
import { getTopicTags } from "../utils/topicTaggingUtils";
import { buildReflectionData } from "../utils/reflectionDataUtils";
import type { ReflectionStep } from "../constants";
import { supabase } from "@/lib/supabase";
import type {
	ContributingFactor,
	Signal,
	CorrectiveAction,
	AssignedTopic,
	ReflectionTheme,
} from "./useReflectionState";

interface UseReflectionPersistenceExtendedProps {
	user: { id: string } | null;
	currentReflectionId: string | null;
	reflectionIdRef: React.MutableRefObject<string | null>;
	setCurrentReflectionId: (id: string | null) => void;
	currentStepRef: React.MutableRefObject<ReflectionStep>;
	setIsSaving: (saving: boolean) => void;
	setLastSaved: (date: Date | null) => void;
	setIsDraft: (draft: boolean) => void;
	selectedCategory: string | null;
	reflectionTitle: string;
	reflectionDescription: string;
	futureState: string;
	qaHistory: Array<{ question: string; answer: string }>;
	currentQuestion: string;
	currentAnswer: string;
	conversationHistory: Array<{ role: string; content: string }>;
	selectedTheme: ReflectionTheme | null;
	contributingFactors: ContributingFactor[];
	signals: Signal[];
	correctiveActions: CorrectiveAction[];
	topicAssignmentId: string | null;
	assignedTopic: AssignedTopic | null;
	draftId: string | undefined;
	saveInProgressRef: React.MutableRefObject<boolean>;
	saveMutexRef: React.MutableRefObject<Promise<void> | null>;
}

/**
 * Extended hook for reflection persistence that handles saveReflectionDraft,
 * completeReflection, and ensureReflectionOwnedByUser
 */
export function useReflectionPersistenceExtended({
	user,
	currentReflectionId,
	reflectionIdRef,
	setCurrentReflectionId,
	currentStepRef,
	setIsSaving,
	setLastSaved,
	setIsDraft,
	selectedCategory,
	reflectionTitle,
	reflectionDescription,
	futureState,
	qaHistory,
	currentQuestion,
	currentAnswer,
	conversationHistory,
	selectedTheme,
	contributingFactors,
	signals,
	correctiveActions,
	topicAssignmentId,
	assignedTopic,
	draftId,
	saveInProgressRef,
	saveMutexRef,
}: UseReflectionPersistenceExtendedProps) {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	/**
	 * Save reflection draft with mutex to prevent concurrent saves
	 */
	const saveReflectionDraft = useCallback(
		async (
			saveReason?: string,
			overrideData?: {
				title?: string;
				description?: string;
				future_state?: string;
			},
		) => {
			if (!user) return;

			// Serialize saves across renders using saveMutexRef
			const previousPromise: Promise<void> =
				saveMutexRef.current || Promise.resolve();
			let release: () => void;
			const nextPromise = new Promise<void>((res) => (release = res));
			saveMutexRef.current = previousPromise.then(() => nextPromise);

			// Wait for previous save to finish
			await previousPromise;

			// Prevent concurrent saves from creating duplicate reflections
			if (saveInProgressRef.current) {
				console.log("saveReflectionDraft: save already in progress, skipping");
				release!();
				return;
			}

			saveInProgressRef.current = true;
			setIsSaving(true);

			if (saveReason) {
				debugLog("💾 Saving reflection:", saveReason);
			}

			try {
				// Capture step synchronously from ref to avoid stale state during async operations
				const stepNow = currentStepRef.current;

				// Get topic tags (with auto-tagging from group assignments)
				const topicTags = await getTopicTags(user, selectedCategory);

				// Use override data if provided (for cases where state hasn't updated yet)
				const titleToSave = overrideData?.title ?? reflectionTitle;
				const descriptionToSave =
					overrideData?.description ?? reflectionDescription;
				const futureStateToSave = overrideData?.future_state ?? futureState;

				debugLog("💾 DETAILED SAVE DEBUG:", {
					hasOverrideData: !!overrideData,
					overrideDataTitle: overrideData?.title,
					overrideDataDescription: overrideData?.description
						? overrideData.description.substring(0, 50)
						: "NOT PROVIDED",
					overrideDataFutureState: overrideData?.future_state
						? overrideData.future_state.substring(0, 50)
						: "NOT PROVIDED",
					stateTitle: reflectionTitle,
					stateDescription: reflectionDescription
						? reflectionDescription.substring(0, 50)
						: "[EMPTY]",
					stateFutureState: futureState
						? futureState.substring(0, 50)
						: "[EMPTY]",
					finalTitleToSave: titleToSave,
					finalDescriptionToSave: descriptionToSave
						? descriptionToSave.substring(0, 50)
						: "[EMPTY]",
					finalFutureStateToSave: futureStateToSave
						? futureStateToSave.substring(0, 50)
						: "[EMPTY]",
				});

				// Build reflection data structure
				const { serviceData } = buildReflectionData({
					title: titleToSave,
					description: descriptionToSave,
					futureState: futureStateToSave,
					qaHistory,
					currentQuestion,
					currentAnswer,
					conversationHistory,
					selectedCategory,
					selectedTheme,
					currentStep: stepNow,
					contributingFactors,
					signals,
					correctiveActions,
					topicTags,
				});

				console.log(`💾 Saving reflection with:`, {
					title: serviceData.title,
					description: serviceData.description
						? serviceData.description.substring(0, 100) + "..."
						: "[EMPTY]",
					topicTags,
					stepNow,
					saveReason,
					usingOverrideData: !!overrideData,
				});

				// Prefer the ref ID if available to prevent duplicate inserts when state hasn't updated yet
				let reflectionId = currentReflectionId || reflectionIdRef.current;

				// Use service to save reflection
				const result = await saveReflectionService(
					reflectionId,
					serviceData,
					user.id,
				);

				if (result.error) {
					throw result.error;
				}

				reflectionId = result.id;

				// Update both ref and state immediately to avoid race conditions
				if (reflectionId) {
					reflectionIdRef.current = reflectionId;
					setCurrentReflectionId(reflectionId);

					// Update URL to include reflection ID for future refreshes (only for new reflections)
					if (!currentReflectionId && !draftId) {
						debugLog("Updating URL to include reflection ID:", reflectionId);
						const newPath = `/reflection/continue/${reflectionId}`;
						const currentParams = new URLSearchParams(searchParams.toString());

						// Preserve the current step in URL
						if (stepNow !== "intro") {
							currentParams.set("step", stepNow);
						}
						// Mark this as a fresh reflection to avoid restoring any previous QA state
						currentParams.set("fresh", "1");

						const newUrl = currentParams.toString()
							? `${newPath}?${currentParams.toString()}`
							: newPath;
						navigate(newUrl, { replace: true });
					}
				}

				// Save contributing factors using service
				if (contributingFactors.length > 0 && reflectionId) {
					try {
						const factorResult = await saveContributingFactorsService(
							reflectionId,
							contributingFactors,
							user.id,
						);
						if (!factorResult.success) {
							debugWarn(
								"Error saving contributing factors:",
								factorResult.error,
							);
						}
					} catch (factorError) {
						debugWarn("Error saving contributing factors:", factorError);
					}
				}

				// Save signals using service
				if (signals.length > 0 && reflectionId) {
					try {
						const signalResult = await saveSignalsService(
							reflectionId,
							signals,
							user.id,
						);
						if (!signalResult.success) {
							debugWarn("Error saving signals:", signalResult.error);
						}
					} catch (signalError) {
						debugWarn("Error saving signals:", signalError);
					}
				}

				// Save corrective actions using service
				if (correctiveActions.length > 0 && reflectionId) {
					try {
						const actionResult = await saveCorrectiveActionsService(
							reflectionId,
							correctiveActions,
							user.id,
						);
						if (!actionResult.success) {
							debugWarn("Error saving corrective actions:", actionResult.error);
						}
					} catch (actionError) {
						debugWarn("Error saving corrective actions:", actionError);
					}
				}

				setLastSaved(new Date());
				// Only mark as draft if not on the final summary step
				if (currentStepRef.current !== "summary") {
					setIsDraft(true);
				}
			} catch (error) {
				console.error(
					"Error saving reflection draft:",
					(error as any)?.message || error,
					error,
				);
			} finally {
				setIsSaving(false);
				// Allow subsequent saves
				saveInProgressRef.current = false;
				// Release the mutex so the next queued save can proceed
				try {
					release!();
				} catch (e) {
					// ignore
				}
				// Clear the mutex reference so future calls use a fresh chain
				saveMutexRef.current = null;
			}
		},
		[
			user,
			currentReflectionId,
			reflectionIdRef,
			setCurrentReflectionId,
			currentStepRef,
			setIsSaving,
			setLastSaved,
			setIsDraft,
			selectedCategory,
			reflectionTitle,
			reflectionDescription,
			futureState,
			qaHistory,
			currentQuestion,
			currentAnswer,
			conversationHistory,
			selectedTheme,
			contributingFactors,
			signals,
			correctiveActions,
			draftId,
			saveInProgressRef,
			saveMutexRef,
			navigate,
			searchParams,
		],
	);

	/**
	 * Complete reflection (mark as not draft)
	 */
	const completeReflection = useCallback(async () => {
		if (!currentReflectionId) {
			console.error("❌ Cannot complete reflection - no currentReflectionId");
			return;
		}

		if (!user?.id) {
			console.error("❌ Cannot complete reflection - no authenticated user");
			return;
		}

		debugLog(
			"🎯 Starting completeReflection for ID:",
			currentReflectionId,
			"User:",
			user.id,
		);

		try {
			// Mark reflection as complete (not draft) - with user_id filter for security
			debugLog("📝 Updating reflection is_draft to false...");
			const { data, error } = await supabase
				.from("reflections")
				.update({ is_draft: false })
				.eq("id", currentReflectionId)
				.eq("user_id", user.id) // Security: only update if owned by current user
				.select();

			if (error) {
				console.error("❌ Error updating is_draft:", error);
				throw error;
			}

			if (!data || data.length === 0) {
				console.error(
					"❌ No reflection was updated - check if reflection exists and user owns it",
				);
				console.error(
					"Reflection ID:",
					currentReflectionId,
					"User ID:",
					user.id,
				);
				return;
			}

			console.log("✅ Database update successful:", data);
			console.log("Updated reflection is_draft value:", data[0]?.is_draft);

			// If this reflection was for an assigned topic, mark it as completed

			setIsDraft(false);
			console.log(
				"✅ Reflection marked as complete - local state updated to isDraft: false",
			);
		} catch (error) {
			console.error("❌ Error completing reflection:", error);
			console.error("Error details:", JSON.stringify(error, null, 2));
		}
	}, [currentReflectionId, user, topicAssignmentId, assignedTopic, setIsDraft]);

	return {
		saveReflectionDraft,
		completeReflection,
	};
}
